import { format, isBefore, startOfMonth } from "date-fns";
import { ClockIcon, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TimeEntry } from "@/types/time-entry";
import { formatDuration } from "@/utils/formatDuration";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface TimeEntryItemProps {
  entry: TimeEntry;
  onDelete: (id: string) => void;
  onEdit: (entry: TimeEntry) => void;
}

export function TimeEntryItem({ entry, onDelete, onEdit }: TimeEntryItemProps) {
  // Entry is editable if it's not before the start of the current month
  const isEditable = !isBefore(new Date(entry.start_time), startOfMonth(new Date()));

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between rounded-lg border bg-white p-4 text-sm gap-4 transition-colors duration-150 hover:bg-gray-50">
      <div className="flex items-start space-x-3 w-full sm:w-auto">
        <ClockIcon className="h-4 w-4 text-gray-500 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="font-medium">
            {format(new Date(entry.start_time), "dd.MM.yyyy")}
          </div>
          <div className="text-gray-500">
            {format(new Date(entry.start_time), "HH:mm")} –{" "}
            {entry.end_time ? format(new Date(entry.end_time), "HH:mm") : "Laufend"}
          </div>
          {entry.description && (
            <div className="mt-2 text-gray-600 break-words" style={{ wordBreak: "break-word" }}>
              {entry.description}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-3 w-full sm:w-auto justify-end">
        {entry.duration && (
          <div className="text-gray-600 whitespace-nowrap">
            {formatDuration(entry.duration)}
          </div>
        )}
        {isEditable && (
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(entry)}
              className="h-8 w-8 p-0"
            >
              <span className="sr-only">Bearbeiten</span>
              <Pencil className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                >
                  <span className="sr-only">Löschen</span>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Zeiterfassung löschen</AlertDialogTitle>
                  <AlertDialogDescription>
                    Möchtest du diesen Zeiterfassungseintrag wirklich löschen?
                    Diese Aktion kann nicht rückgängig gemacht werden.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(entry.id)}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    Löschen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    </div>
  );
}
